import * as RadioGroup from "@radix-ui/react-radio-group";
import { atom, useAtom } from "jotai";
import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth";
import { Actions, Close, DialogNoTrigger } from "../../components/Dialog";
import { Form, Input, Submit } from "../../components/Form";
import { MenuItem } from "../../components/Menu";
import { Radio } from "../../components/Radio";
import {
  colorValue,
  resolveUserColor,
  UserColor,
  USER_COLORS,
} from "../../css/color";
import { currentUserAtom } from "../../jotai/atoms";
import { useUserInfo } from "../../liveblocks/maps";
import { styled } from "../../stitches.config";
import { User } from "../../types";

const nameDialogOpenAtom = atom(false);

export function EditNameDialogTrigger() {
  const [, setOpen] = useAtom(nameDialogOpenAtom);
  return (
    <MenuItem
      aria-label="Edit your display name"
      onSelect={() => {
        // hack to make sure autofocus works properly in dialog
        // when dropdown menu closes it'll try to reclaim focus
        // to its trigger, which confuses this dialog's autofocus
        setTimeout(() => setOpen(true), 1);
      }}
    >
      Edit name
    </MenuItem>
  );
}

export function EditNameDialog() {
  const [open, setOpen] = useAtom(nameDialogOpenAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const [info, infoClient] = useUserInfo(currentUser?.id);
  const { update } = useAuth();

  if (!info) return null;

  function setName(name: string) {
    infoClient?.set("name", name);

    if (!currentUser?.anonymous) {
      update({ name });
    }

    setOpen(false);
  }

  return (
    <DialogNoTrigger title="Edit name" open={open} onOpenChange={setOpen}>
      <NameForm callback={setName} currentName={info.name} />
    </DialogNoTrigger>
  );
}

const colorDialogOpenAtom = atom(false);

export function EditColorDialogTrigger() {
  const [, setOpen] = useAtom(colorDialogOpenAtom);
  return (
    <MenuItem
      aria-label="Edit your cursor color"
      onSelect={() => {
        // hack to make sure autofocus works properly in dialog
        // when dropdown menu closes it'll try to reclaim focus
        // to its trigger, which confuses this dialog's autofocus
        setTimeout(() => setOpen(true), 1);
      }}
    >
      Edit color
    </MenuItem>
  );
}

function resolveColor(color?: User["color"]): UserColor {
  const c = resolveUserColor(color);
  return c;
}

export function EditColorDialog() {
  const [open, setOpen] = useAtom(colorDialogOpenAtom);
  const [currentUser] = useAtom(currentUserAtom);
  const [info, infoClient] = useUserInfo(currentUser?.id);

  // cache starting color, so it can be reset to
  const [startingColor, setStartingColor] = useState<UserColor>(
    resolveColor(info?.color)
  );

  useEffect(() => {
    if (open === true) {
      setStartingColor(resolveColor(info?.color));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!info) return null;

  function setColor(color: UserColor) {
    infoClient?.set("color", color);
  }

  function setOpenCB(_open: boolean) {
    setOpen(_open);
    // if form was cancelled, reset color to starting color
    if (_open === false && startingColor) {
      setColor(startingColor);
    }
  }

  function persistColor() {
    // The response is marked `as string` to silence Typescript warning.
    // The LiveBlocks client currently returns any possible type for the response, which includes booleans and other types.
    const color = infoClient?.get("color");
    if (!color) return;
    setStartingColor(color as UserColor);
    setOpen(false);
  }
  return (
    <DialogNoTrigger title="Edit color" open={open} onOpenChange={setOpenCB}>
      <ColorForm
        changeCallback={setColor}
        persistCallBack={persistColor}
        currentColor={info.color}
      />
    </DialogNoTrigger>
  );
}

export function NameForm({
  callback,
  label,
  currentName = "",
}: {
  callback: (name: string) => void;
  label?: string;
  currentName?: string;
}) {
  const [name, setName] = useState(currentName);

  function onChange(e: React.ChangeEvent<HTMLInputElement>) {
    setName(e.target.value);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    callback(name);
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Input
        name="name"
        aria-label="Name or handle"
        type="text"
        placeholder="Type a name or handle..."
        required
        onChange={onChange}
        value={name}
        autoComplete="name"
      />
      <Actions>
        <Submit>{label ? label : "Save"}</Submit>
        {currentName && <Close secondary>Cancel</Close>}
      </Actions>
    </Form>
  );
}

interface ColorFormProps {
  changeCallback: (color: UserColor) => void;
  persistCallBack: () => void;
  currentColor: string;
}

function ColorForm({
  changeCallback,
  persistCallBack,
  currentColor,
}: ColorFormProps) {
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    persistCallBack();
  }

  return (
    <Form onSubmit={handleSubmit}>
      <Radio
        onChange={(value) => changeCallback(value as UserColor)}
        defaultValue={currentColor}
      >
        <ColorRadioItems colors={USER_COLORS} current={currentColor} />
      </Radio>
      <Actions>
        <Submit>Save</Submit>
        <Close secondary>Cancel</Close>
      </Actions>
    </Form>
  );
}

export function ColorRadioItems({
  colors,
  current,
}: {
  colors: readonly string[];
  current?: string;
}) {
  return (
    <>
      {colors.map((color) => (
        <RadioItemColorWell
          autoFocus={current === color}
          value={color}
          key={color}
          style={{
            background: colorValue(color, 80),
          }}
        >
          <Indicator />
        </RadioItemColorWell>
      ))}
    </>
  );
}

const RadioItemColorWell = styled(RadioGroup.Item, {
  width: "$6",
  height: "$6",
  display: "inline-flex",
  verticalAlign: "middle",
  borderRadius: "$circle",
  appearance: "none",
  border: "1px solid transparent",
  position: "relative",
  boxShadow: "inset 0 0 1px 0 rgba(0,0,0,.5)",

  "& ~ &": { marginLeft: "$2" },

  "&:focus": {},
});

const Indicator = styled(RadioGroup.Indicator, {
  position: "absolute",
  top: "-$1",
  bottom: "-$1",
  right: "-$1",
  left: "-$1",
  borderRadius: "$circle",
  border: "2px solid $paper60",
});
